import { ApolloClient, createHttpLink, InMemoryCache, Observable, FetchResult } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { fromPromise } from '@apollo/client/link/utils';

import { AUTH_TOKEN, REFRESH_TOKEN } from './constants';
import { authProvider } from './auth';

import { BACKEND_URL } from './config';
import { REFRESH_MUTATION } from './queries/getRefresh';

const httpLink = createHttpLink({
  uri: BACKEND_URL,
});

const refreshClient = new ApolloClient({
  uri: BACKEND_URL,
  cache: new InMemoryCache(),
});

// Refresh Token Logic
const refreshToken = async () => {
  console.log("Attempting to refresh token...");
  try {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN);
    if (!refreshToken) throw new Error('No refresh token available');

    const refreshRequest = await refreshClient.mutate({ mutation: REFRESH_MUTATION, variables: { refreshToken } });
    console.log("Refresh Request:", refreshRequest);

    if (refreshRequest?.data?.refresh?.success) {
      await authProvider.signin(refreshRequest?.data?.refresh?.token, refreshRequest?.data?.refresh?.refreshToken);
    }

    return refreshRequest?.data?.refresh?.token
  } catch (error) {
    // Clear tokens and handle logout
    localStorage.removeItem(AUTH_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    // You might want to redirect to login page here
    throw error;
  }
};

// Attempt a token refresh and, on success, replay the failed operation.
const refreshAndRetry = (operation: any, forward: any): Observable<FetchResult> =>
  fromPromise(
    refreshToken().catch((_) => null) // swallow; null = refresh failed
  )
    .filter(accessToken => Boolean(accessToken)) // only proceed if we got a new access token
    .flatMap(() => {
      return forward(operation);
    });

// Error Link
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  console.log("Error link triggered", { graphQLErrors, networkError, operation });

  // Resolver-level auth errors (HTTP 200 with an errors array).
  if (graphQLErrors?.some(
    ({ extensions }) => extensions?.code === 'UNAUTHENTICATED' || extensions?.code === 'FORBIDDEN'
  )) {
    return refreshAndRetry(operation, forward);
  }

  // An expired access token surfaces as a non-2xx response, which Apollo Client
  // reports as a networkError (not graphQLErrors). The server returns 401 for an
  // expired token; 403 is kept for backwards compatibility.
  const statusCode = (networkError as { statusCode?: number } | null)?.statusCode;
  if (statusCode === 401 || statusCode === 403) {
    return refreshAndRetry(operation, forward);
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem(AUTH_TOKEN);
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    }
  }
});

// The single Apollo Client instance backing <ApolloProvider>. Login/Signup mutate and
// resetStore() through this same instance (rather than a standalone client) so that
// Layout's GET_ME watch — and every role hook reading the same cache — actually sees
// post-login data instead of the stale unauthenticated `me` response.
export const client = new ApolloClient({
  link: errorLink.concat(authLink).concat(httpLink),
  cache: new InMemoryCache()
});
